import { useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { GoogleLogo } from '@phosphor-icons/react';
import Logo from '../shared/components/Logo';
import SignupModal, { type LoginData, type SignupData } from './SignupModal';
import introMain from '../../images/intromain.png';
import MainURL from '../../MainURL';
import {
  drumActivePlanAtom,
  drumLanguageAtom,
  drumLoggedInAtom,
  drumSelectedSongAtom,
  drumUserPreferencesAtom,
  drumUserProfileAtom,
  drumUserTokenAtom,
  type DrumUserPreferences,
  type DrumUserProfile,
} from '../shared/store/drumAppAtoms';
import { fetchMemberSession } from '../shared/api/memberApi';
import { applyMemberSessionData } from '../shared/hooks/useMemberSession';
import '../shared/styles/DrumApp.scss';
import './IntroLogin.scss';

function IntroLogin() {
  const loggedIn = useAtomValue(drumLoggedInAtom);
  const token = useAtomValue(drumUserTokenAtom);
  const setLoggedIn = useSetAtom(drumLoggedInAtom);
  const setSelectedSong = useSetAtom(drumSelectedSongAtom);
  const setUserToken = useSetAtom(drumUserTokenAtom);
  const setUserProfile = useSetAtom(drumUserProfileAtom);
  const setPreferences = useSetAtom(drumUserPreferencesAtom);
  const setLanguage = useSetAtom(drumLanguageAtom);
  const setActivePlan = useSetAtom(drumActivePlanAtom);
  const navigate = useNavigate();
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isAutoLogging, setIsAutoLogging] = useState(false);

  const hasSavedSession = Boolean(loggedIn && token);
  const sessionSetters = { setUserProfile, setPreferences, setLanguage, setActivePlan };

  const enterApp = (data?: { token?: string; user?: DrumUserProfile; preferences?: DrumUserPreferences }) => {
    if (data?.token) setUserToken(data.token);
    applyMemberSessionData(data || {}, sessionSetters);
    setSelectedSong(null);
    setLoggedIn(true);
    navigate('/drum/library');
  };

  const clearSavedSession = () => {
    setLoggedIn(false);
    setUserToken('');
    setUserProfile(null);
  };

  const handleSignupClose = () => setIsSignupOpen(false);

  const handleLoginPress = async () => {
    if (isAutoLogging) return;
    if (!hasSavedSession) {
      setIsSignupOpen(true);
      return;
    }

    setIsAutoLogging(true);
    try {
      const session = await fetchMemberSession(token);
      enterApp({ token, ...session });
    } catch {
      clearSavedSession();
      setIsSignupOpen(true);
    } finally {
      setIsAutoLogging(false);
    }
  };

  const postAuth = async (path: 'login' | 'signup', body: LoginData | SignupData, fallbackMessage: string) => {
    let response: Response;
    try {
      response = await fetch(`${MainURL}/auth/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      throw new Error(fallbackMessage);
    }

    const responseData = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(responseData?.message || fallbackMessage);
    }

    enterApp(responseData);
  };

  const handleLoginSubmit = (data: LoginData) => (
    postAuth('login', data, '로그인에 실패했습니다.')
  );

  const handleSignupSubmit = (data: SignupData) => (
    postAuth('signup', data, '회원가입에 실패했습니다.')
  );

  return (
    <div className="drum-app" data-localized-root>
      <main className="dh-login-screen">
        <div className="login-art"><img src={introMain} alt="Outdoor rhythm stage" /></div>
        <section className="login-panel">
          <Logo />
          <h1>Rhythm that moves you.</h1>
          <button className="google-button" type="button" onClick={handleLoginPress} disabled={isAutoLogging}>
            <GoogleLogo weight="bold" /> {isAutoLogging ? '로그인 중...' : 'Continue with Google'}
          </button>
          <button className="google-button admin-button" type="button" onClick={() => navigate('/admin')}>관리자페이지</button>
          <footer>Copyright © JUEUNEDUTECH. All rights reserved.</footer>
        </section>
      </main>
      {isSignupOpen && (
        <SignupModal onClose={handleSignupClose} onLogin={handleLoginSubmit} onSignup={handleSignupSubmit} />
      )}
    </div>
  );
}

export default IntroLogin;
